import React, { createContext, useContext, ReactNode } from 'react'
import { useUser } from '../hooks/useUser'
import { useInvestments } from '../hooks/useInvestments'

interface DatabaseContextType {
  user: ReturnType<typeof useUser>
  investments: ReturnType<typeof useInvestments>
}

const DatabaseContext = createContext<DatabaseContextType | null>(null)

export function DatabaseProvider({ children }: { children: ReactNode }) {
  const user = useUser()
  const investments = useInvestments(user.user?.id || null)


  return (
    <DatabaseContext.Provider value={{ user, investments }}>
      {children}
    </DatabaseContext.Provider>
  )
}

export function useDatabase() {
  const context = useContext(DatabaseContext)
  if (!context) {
    // During HMR or in edge cases the provider may be temporarily missing.
    // Return a safe fallback instead of throwing to avoid hard crashes.
    // Consumers should handle `loading` and `isAuthenticated` accordingly.
    const safeUser: any = {
      isAuthenticated: false,
      user: null,
      profile: null,
      loading: true,
      error: null,
      updateUser: async () => {},
      updateProfile: async () => {},
      signOut: () => {},
    }

    const safeInvestments: any = {
      investments: [],
      portfolio: null,
      transactions: [],
      metrics: {},
      createInvestment: async () => {},
      addTransaction: async () => {},
      loading: true,
      error: null,
    }

    return { user: safeUser, investments: safeInvestments }
  }

  return context
}

// Hook for authenticated data (throws if not authenticated)
export function useAuthenticatedData() {
  const { user, investments } = useDatabase()
  
  if (!user.isAuthenticated) {
    throw new Error('User must be authenticated to access this data')
  }
  
  return {
    user: user.user!,
    profile: user.profile,
    investments: investments.investments,
    portfolio: investments.portfolio,
    transactions: investments.transactions,
    metrics: investments.metrics,
    updateUser: user.updateUser,
    updateProfile: user.updateProfile,
    createInvestment: investments.createInvestment,
    addTransaction: investments.addTransaction,
    signOut: user.signOut,
    loading: user.loading || investments.loading,
    error: user.error || investments.error
  }
}